<script setup>
    import {
        BorrowingIcon,
        BundleIcon,
        ClientIcon,
        MerchandiseIcon,
        OrderIcon,
        ProductIcon,
        ServiceIcon,
    } from '@/components/icons'
    import { debounce } from '@/utils/Debounce'
    import { ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline'
    import { Link } from '@inertiajs/vue3'
    import axios from 'axios'
    import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

    // STATE
    const searchQuery = ref('')
    const rawResults = ref([])
    const loading = ref(false)
    const open = ref(false)
    const errorMessage = ref('')
    const dropdownRef = ref(null)
    const inputRef = ref(null)
    const inputFocused = ref(false)
    const activeIndex = ref(-1)
    let abortController = null

    // CONFIG
    const MIN_LENGTH = 2

    // GROUPING
    const grouped = computed(() => {
        const g = { borrowing: [], product: [], bundle: [], client: [], order: [], service: [], merchandise: [] }
        rawResults.value.forEach((r) => {
            if (g[r.type]) {
                g[r.type].push(r)
            }
        })
        return g
    })
    const flatOrdered = computed(() => [
        ...grouped.value.borrowing,
        ...grouped.value.product,
        ...grouped.value.bundle,
        ...grouped.value.client,
        ...grouped.value.order,
        ...grouped.value.service,
        ...grouped.value.merchandise,
    ])
    const nonEmptyGroupCount = computed(
        () =>
            ['borrowing', 'product', 'bundle', 'client', 'order', 'service', 'merchandise'].filter(
                (t) => grouped.value[t].length
            ).length
    )
    const hasResults = computed(() => flatOrdered.value.length > 0)

    // ACTIVE STATE HELPERS
    function isActive(type, id) {
        const current = flatOrdered.value[activeIndex.value]
        return !!current && current.type === type && current.id === id
    }

    function highlight(text) {
        const q = searchQuery.value.trim()
        if (!q) return text
        try {
            const re = new RegExp('(' + q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + ')', 'ig')
            return text.replace(re, '<mark class="bg-yellow-200 rounded px-0.5">$1</mark>')
        } catch {
            return text
        }
    }

    // SEARCH
    const performSearch = async () => {
        if (abortController) abortController.abort()
        abortController = new AbortController()
        loading.value = true
        errorMessage.value = ''
        try {
            const response = await axios.get(route('api.search'), {
                params: {
                    query: searchQuery.value.trim(),
                    models: ['borrowings', 'products', 'bundles', 'clients', 'orders', 'services', 'merchandises'],
                },
                signal: abortController.signal,
            })

            const data = response.data || {}

            const mapped = []
            if (Array.isArray(data.borrowings)) {
                data.borrowings.forEach((b) => {
                    mapped.push({
                        type: 'borrowing',
                        id: b.id,
                        label: b.barcode ? `#${b.barcode}` : `Výpůjčka #${b.id}`,
                        barcode: b.barcode || '',
                        url: route('borrowings.show', b.id),
                    })
                })
            }
            if (Array.isArray(data.products)) {
                data.products.forEach((p) => {
                    mapped.push({
                        type: 'product',
                        id: p.id,
                        label: p.name || `Produkt #${p.id}`,
                        barcode: p.barcode || '',
                        url: route('products.show', p.id),
                    })
                })
            }
            if (Array.isArray(data.bundles)) {
                data.bundles.forEach((bu) => {
                    mapped.push({
                        type: 'bundle',
                        id: bu.id,
                        label: bu.name || `Balíček #${bu.id}`,
                        barcode: bu.barcode || '',
                        url: route('bundles.show', bu.id),
                    })
                })
            }
            if (Array.isArray(data.clients)) {
                data.clients.forEach((c) => {
                    mapped.push({
                        type: 'client',
                        id: c.id,
                        label: c.first_name + ' ' + c.last_name || `Klient #${c.id}`,
                        barcode: c.email || '',
                        url: route('clients.show', c.id),
                    })
                })
            }
            if (Array.isArray(data.orders)) {
                data.orders.forEach((o) => {
                    mapped.push({
                        type: 'order',
                        id: o.id,
                        label: o.barcode ? `#${o.barcode}` : `Objednávka #${o.id}`,
                        barcode: o.barcode || '',
                        url: route('orders.show', o.id),
                    })
                })
            }
            if (Array.isArray(data.services)) {
                data.services.forEach((s) => {
                    mapped.push({
                        type: 'service',
                        id: s.id,
                        label: s.name || `Služba #${s.id}`,
                        barcode: s.barcode || '',
                        url: route('services.show', s.id),
                    })
                })
            }
            if (Array.isArray(data.merchandises)) {
                data.merchandises.forEach((m) => {
                    mapped.push({
                        type: 'merchandise',
                        id: m.id,
                        label: m.name || `Zboží #${m.id}`,
                        barcode: m.barcode || '',
                        url: route('merchandises.show', m.id),
                    })
                })
            }
            rawResults.value = mapped
            activeIndex.value = -1
            open.value = true
        } catch (e) {
            if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
                errorMessage.value = 'Chyba při vyhledávání'
            }
        } finally {
            loading.value = false
        }
    }
    const debouncedSearch = debounce(performSearch, 300)
    function handleInput() {
        if (!searchQuery.value.trim() || searchQuery.value.length < MIN_LENGTH) {
            rawResults.value = []
            open.value = false
            activeIndex.value = -1
            return
        }
        debouncedSearch()
    }
    function clearSearch() {
        searchQuery.value = ''
        rawResults.value = []
        open.value = false
        activeIndex.value = -1
        inputRef.value?.focus()
    }

    // KEYBOARD
    function handleKeydown(e) {
        if (!open.value) return
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) e.preventDefault()
        if (e.key === 'ArrowDown') {
            if (!flatOrdered.value.length) return
            activeIndex.value = (activeIndex.value + 1) % flatOrdered.value.length
            scrollActiveIntoView()
        } else if (e.key === 'ArrowUp') {
            if (!flatOrdered.value.length) return
            activeIndex.value = (activeIndex.value - 1 + flatOrdered.value.length) % flatOrdered.value.length
            scrollActiveIntoView()
        } else if (e.key === 'Enter') {
            if (activeIndex.value >= 0) {
                window.location.href = flatOrdered.value[activeIndex.value].url
            }
        } else if (e.key === 'Escape') {
            open.value = false
        }
    }
    function scrollActiveIntoView() {
        requestAnimationFrame(() => {
            const el = document.querySelector('[data-active="true"]')
            if (el) {
                el.scrollIntoView({ block: 'nearest' })
            }
        })
    }

    // OUTSIDE CLICK
    const handleGlobalClick = (event) => {
        if (!open.value) return
        if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
            open.value = false
        }
    }

    onMounted(() => {
        document.addEventListener('mousedown', handleGlobalClick)
        document.addEventListener('keydown', handleKeydown)
    })

    onUnmounted(() => {
        document.removeEventListener('mousedown', handleGlobalClick)
        document.removeEventListener('keydown', handleKeydown)
    })

    watch(inputFocused, (val) => {
        if (val && searchQuery.value.length >= MIN_LENGTH && hasResults.value) {
            open.value = true
        }
    })
</script>
<template>
    <div class="fixed top-0 right-0 z-90 hidden h-20 w-full border-b border-gray-200 bg-white lg:block lg:pl-72">
        <div class="container mx-auto flex h-full items-center px-4 sm:px-6 lg:px-8">
            <div
                ref="dropdownRef"
                class="relative w-full max-w-xl"
            >
                <div
                    class="flex items-center rounded-md border border-gray-300 bg-white px-3 shadow-sm focus-within:border-green-500 focus-within:ring-1 focus-within:ring-green-500"
                >
                    <MagnifyingGlassIcon class="mr-2 size-5 text-gray-400" />
                    <input
                        id="global-search"
                        ref="inputRef"
                        v-model="searchQuery"
                        @input="handleInput"
                        @focus="inputFocused = true"
                        @blur="inputFocused = false"
                        type="search"
                        autocomplete="off"
                        placeholder="Hledat výpůjčku, produkt, balíček..."
                        class="h-10 w-full flex-1 border-0 bg-transparent p-0 text-sm focus:ring-0 focus:outline-none"
                    />
                    <button
                        v-if="searchQuery"
                        type="button"
                        @click="clearSearch"
                        class="ml-2 rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                        Vymazat
                    </button>
                </div>

                <Transition
                    enter-active-class="transition ease-out duration-150"
                    enter-from-class="opacity-0 translate-y-1"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition ease-in duration-100"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 translate-y-1"
                >
                    <div
                        v-if="
                            open &&
                            (loading ||
                                hasResults ||
                                errorMessage ||
                                (!loading && !hasResults && searchQuery.length >= MIN_LENGTH))
                        "
                        class="absolute top-12 right-0 left-0 z-50 w-[60rem] overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg"
                    >
                        <div
                            v-if="loading"
                            class="space-y-3 p-4"
                        >
                            <div class="h-4 w-1/3 animate-pulse rounded bg-gray-200" />
                            <div class="flex gap-3">
                                <div class="h-10 w-10 animate-pulse rounded bg-gray-200" />
                                <div class="flex-1 space-y-2">
                                    <div class="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
                                    <div class="h-3 w-1/2 animate-pulse rounded bg-gray-200" />
                                </div>
                            </div>
                            <div class="flex gap-3">
                                <div class="h-10 w-10 animate-pulse rounded bg-gray-200" />
                                <div class="flex-1 space-y-2">
                                    <div class="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
                                    <div class="h-3 w-1/3 animate-pulse rounded bg-gray-200" />
                                </div>
                            </div>
                        </div>

                        <div
                            v-else
                            class="p-2"
                        >
                            <div
                                v-if="hasResults"
                                :class="[
                                    'grid gap-3',
                                    nonEmptyGroupCount === 1 && 'grid-cols-1',
                                    nonEmptyGroupCount === 2 && 'grid-cols-1 md:grid-cols-2',
                                    nonEmptyGroupCount >= 3 && 'grid-cols-1 md:grid-cols-3',
                                ]"
                            >
                                <div
                                    v-if="grouped.borrowing.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 font-semibold tracking-wide text-indigo-600 uppercase"
                                    >
                                        <BorrowingIcon class="mr-2 size-5" />
                                        Výpůjčky
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.borrowing"
                                            :key="'b-' + item.id"
                                            :id="'global-search-item-borrowing-' + item.id"
                                        >
                                            <Link
                                                :href="route('borrowings.show', item.id)"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-indigo-50 focus:outline-none"
                                                :data-active="isActive('borrowing', item.id)"
                                                data-type="borrowing"
                                                role="option"
                                                :aria-selected="isActive('borrowing', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'borrowing' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'borrowing' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.id)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-indigo-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.product.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-green-200 bg-green-50 px-3 py-2 font-semibold tracking-wide text-green-600 uppercase"
                                    >
                                        <ProductIcon class="mr-2 size-5" />
                                        Produkty
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.product"
                                            :key="'p-' + item.id"
                                            :id="'global-search-item-product-' + item.id"
                                        >
                                            <Link
                                                :href="route('products.show', item.id)"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-green-50 focus:outline-none"
                                                :data-active="isActive('product', item.id)"
                                                data-type="product"
                                                role="option"
                                                :aria-selected="isActive('product', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'product' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'product' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.label)"
                                                    />

                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-green-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.bundle.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-rose-200 bg-rose-50 px-3 py-2 font-semibold tracking-wide text-rose-600 uppercase"
                                    >
                                        <BundleIcon class="mr-2 size-5" />
                                        Balíčky
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.bundle"
                                            :key="'bundle-' + item.id"
                                            :id="'global-search-item-bundle-' + item.id"
                                        >
                                            <Link
                                                :href="route('bundles.show', item.id)"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-rose-50 focus:outline-none"
                                                :data-active="isActive('bundle', item.id)"
                                                data-type="bundle"
                                                role="option"
                                                :aria-selected="isActive('bundle', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'bundle' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'bundle' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.label)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-rose-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.client.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-rose-200 bg-rose-50 px-3 py-2 font-semibold tracking-wide text-rose-600 uppercase"
                                    >
                                        <ClientIcon class="mr-2 size-5" />
                                        Klienti
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.client"
                                            :key="'client-' + item.id"
                                            :id="'global-search-item-client-' + item.id"
                                        >
                                            <Link
                                                :href="route('clients.show', item.id)"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-rose-50 focus:outline-none"
                                                :data-active="isActive('client', item.id)"
                                                data-type="client"
                                                role="option"
                                                :aria-selected="isActive('client', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'client' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'client' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.label)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-rose-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.order.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-amber-200 bg-amber-50 px-3 py-2 font-semibold tracking-wide text-amber-600 uppercase"
                                    >
                                        <OrderIcon class="mr-2 size-5" />
                                        Objednávky
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.order"
                                            :key="'order-' + item.id"
                                            :id="'global-search-item-order-' + item.id"
                                        >
                                            <Link
                                                :href="item.url"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-amber-50 focus:outline-none"
                                                :data-active="isActive('order', item.id)"
                                                data-type="order"
                                                role="option"
                                                :aria-selected="isActive('order', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'order' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'order' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.id)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-amber-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.service.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2 font-semibold tracking-wide text-blue-600 uppercase"
                                    >
                                        <ServiceIcon class="mr-2 size-5" />
                                        Služby
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.service"
                                            :key="'service-' + item.id"
                                            :id="'global-search-item-service-' + item.id"
                                        >
                                            <Link
                                                :href="item.url"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-blue-50 focus:outline-none"
                                                :data-active="isActive('service', item.id)"
                                                data-type="service"
                                                role="option"
                                                :aria-selected="isActive('service', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'service' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'service' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.label)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-blue-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>

                                <div
                                    v-if="grouped.merchandise.length"
                                    class="min-w-0"
                                >
                                    <header
                                        class="mb-2 flex items-center rounded-md border border-purple-200 bg-purple-50 px-3 py-2 font-semibold tracking-wide text-purple-600 uppercase"
                                    >
                                        <MerchandiseIcon class="mr-2 size-5" />
                                        Zboží
                                    </header>
                                    <ul class="space-y-1">
                                        <li
                                            v-for="item in grouped.merchandise"
                                            :key="'merchandise-' + item.id"
                                            :id="'global-search-item-merchandise-' + item.id"
                                        >
                                            <Link
                                                :href="item.url"
                                                class="group flex items-start gap-3 rounded-md px-2 py-2 text-sm hover:bg-purple-50 focus:outline-none"
                                                :data-active="isActive('merchandise', item.id)"
                                                data-type="merchandise"
                                                role="option"
                                                :aria-selected="isActive('merchandise', item.id) ? 'true' : 'false'"
                                                @mouseenter="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'merchandise' && r.id === item.id
                                                    )
                                                "
                                                @focus="
                                                    activeIndex = flatOrdered.findIndex(
                                                        (r) => r.type === 'merchandise' && r.id === item.id
                                                    )
                                                "
                                            >
                                                <span class="flex-1">
                                                    <span
                                                        class="font-medium text-gray-900"
                                                        v-html="highlight(item.label)"
                                                    />
                                                    <span
                                                        v-if="item.barcode"
                                                        class="block text-[11px] text-gray-500"
                                                        v-html="highlight(item.barcode)"
                                                    />
                                                </span>
                                                <ChevronRightIcon
                                                    class="size-4 text-gray-300 group-hover:text-purple-400"
                                                />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div
                                v-else
                                class="py-12 text-center"
                            >
                                <div
                                    class="mx-auto mb-3 flex size-12 items-center justify-center rounded-full bg-gray-100"
                                >
                                    <MagnifyingGlassIcon class="size-6 text-gray-400" />
                                </div>
                                <p class="text-sm font-medium text-gray-900">Žádné výsledky</p>
                                <p class="mt-1 text-xs text-gray-500">
                                    Nic se nám bohužel nepovedlo najít pro výraz
                                    <span class="font-semibold">"{{ searchQuery }}"</span>
                                </p>
                            </div>
                        </div>

                        <div
                            class="border-t bg-white px-3 py-2 text-[11px] text-gray-500"
                            id="global-search-legend"
                        >
                            <div class="flex flex-wrap items-center gap-x-4 gap-y-2">
                                <ul class="mt-1 flex gap-1 text-xs text-gray-400">
                                    <li>Použijte</li>
                                    <li>
                                        <kbd class="rounded-xs border border-yellow-200 bg-yellow-100 p-0.5">↑↓</kbd>
                                        pro navigaci,
                                    </li>
                                    <li>
                                        <kbd class="rounded-xs border border-yellow-200 bg-yellow-100 p-0.5">Enter</kbd>
                                        pro výběr,
                                    </li>
                                    <li>
                                        <kbd class="rounded-xs border border-yellow-200 bg-yellow-100 p-0.5">Esc</kbd>
                                        pro zavření
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        </div>
    </div>
</template>

<style scoped>
    [data-active='true'][data-type='borrowing'] {
        background: #eef2ff; /* indigo-50 */
        box-shadow: 0 0 0 1px #c7d2fe inset;
    }
    [data-active='true'][data-type='product'] {
        background: #ecfdf5; /* green-50 */
        box-shadow: 0 0 0 1px #bbf7d0 inset;
    }
    [data-active='true'][data-type='bundle'] {
        background: #fff1f2; /* rose-50 */
        box-shadow: 0 0 0 1px #fecdd3 inset;
    }
    [data-active='true'][data-type='client'] {
        background: #fff1f2; /* rose-50 */
        box-shadow: 0 0 0 1px #fecdd3 inset;
    }
    [data-active='true'][data-type='order'] {
        background: #fffbeb; /* amber-50 */
        box-shadow: 0 0 0 1px #fde68a inset;
    }
    [data-active='true'][data-type='service'] {
        background: #eff6ff; /* blue-50 */
        box-shadow: 0 0 0 1px #bfdbfe inset;
    }
    [data-active='true'][data-type='merchandise'] {
        background: #faf5ff; /* purple-50 */
        box-shadow: 0 0 0 1px #e9d5ff inset;
    }
    [data-active='true'] .font-medium {
        text-decoration: underline;
    }
    kbd.kbd {
        border: 1px solid #d1d5db; /* gray-300 */
        background: #f9fafb; /* gray-50 */
        border-radius: 4px;
        padding: 2px 4px;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
        font-size: 10px;
        line-height: 1;
        box-shadow: 0 1px 0 0 #e5e7eb; /* subtle */
    }
</style>
